/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';

import './ShareCardEdit.styles.scss';

import ShareCardEditComment from './ShareCardEditComment';
import ShareCardEditItems from './ShareCardEditItems';

import { ShareCard } from '../../../../models/ShareCardModel';
import { TrackEvent } from '../../../../utilities/UserMetrics';
import { useShareCardForActiveProfile } from '../../../hooks/useShareCard';
import { useUIState } from '../../../hooks/useUIState';
import { useActiveUser } from '../../../hooks/useUser';
import { useActiveProfile } from '../../../hooks/useProfile';
import { useHealthEntriesForActiveProfile } from '../../../hooks/useHealthEntry';

export interface Props {
  shareCardId: number;
  isCreate: boolean;
  definitionId: number;
  newColorId: number;

  goBackToEdit(): void;
  onSubmit(data, callback): void;
  closeShareCard(): void;
  deleteShareCard(): void;
}

const ShareCardEdit: React.FC<Props> = (props) => {
  const { onSubmit, shareCardId, isCreate, closeShareCard, deleteShareCard, goBackToEdit, definitionId } = props;
  const [{ isActiveProfileMainProfile }] = useUIState();
  const [{ preferredLanguageCode }] = useActiveUser();
  const [, profileActions] = useActiveProfile();
  const [{ groupedHealthEntryList, ...heData }] = useHealthEntriesForActiveProfile();
  const [origShareCardData, scActions] = useShareCardForActiveProfile(shareCardId);

  const [commentState, setCommentState] = React.useState(false);
  const [shareCardDataState, setShareCardData] = React.useState<ShareCard>(null);
  const isDataLoaded = origShareCardData.isLoaded && heData.isLoaded;
  const shareCardColorModel = scActions.getShareCardColor(shareCardDataState, null);
  const hasData = (isCreate || Boolean(origShareCardData.shareCard)) && isDataLoaded;

  React.useEffect(() => {
    if (!hasData || !isDataLoaded) { return; }
    if (isCreate) {
      const newSC = scActions.instantiateNewShareCard(definitionId, props.newColorId);
      setShareCardData(newSC.data);
    } else {
      setShareCardData(origShareCardData.shareCard);
    }
  }, [hasData, isDataLoaded]);

  const updateShareCard = (newData, callback) => {
    setShareCardData(newData);
    if (callback) {
      callback();
    }
  };

  if (!isDataLoaded) { return null; }

  return commentState
    ? (
      <ShareCardEditComment
        shareCardColorModel={shareCardColorModel}
        shareCard={shareCardDataState}
        updateComment={(commentValue) => {
          const newShareCard = scActions.instantiate(shareCardDataState);
          newShareCard.comment.set(commentValue);
          updateShareCard(newShareCard.data, () => {
            setCommentState(false);
          });
        }}
        closeComment={() => { setCommentState(false); }}
      />
    ) : (
      <ShareCardEditItems
        shareCardColorModel={shareCardColorModel}
        shareCardData={shareCardDataState}
        updateShareCard={updateShareCard}
        cancelTitleChange={() => {
          const newShareCard = scActions.instantiate(shareCardDataState);
          const origShareCard = scActions.instantiate();
          newShareCard.title.set(origShareCard.customTitle);
          setShareCardData(newShareCard.data);
        }}
        deleteShareCard={deleteShareCard}
        languageCode={preferredLanguageCode}
        healthEntries={groupedHealthEntryList}
        profile={profileActions.instantiateProfile()}
        isMainProfile={isActiveProfileMainProfile}
        onSubmit={(scData, callback) => {
          const newShareCard = scActions.instantiate(scData);
          const shareCardTemplateUId = newShareCard.definitionUid;

          if (isCreate) {
            TrackEvent.shareCards.createdShareCard(shareCardTemplateUId);
          } else {
            const oldShareCardData = origShareCardData.shareCard;
            TrackEvent.shareCards.alteredShareCard(shareCardTemplateUId, oldShareCardData, shareCardDataState);
          }

          onSubmit(scData, callback);
        }}
        closeShareCard={closeShareCard}
        goToComment={() => { setCommentState(true); }}
        isCreate={isCreate}
        goBackToEdit={goBackToEdit}
      />
    );
};

export default ShareCardEdit;
